import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { REPORT_LIMITS, type HelpDraft, type HelpPage, type ReportKind } from "@joogoos/contracts";
import { api, lastFailureRef } from "../api.js";
import type { onScreen } from "./onScreen.js";

/**
 * The report form: "this is broken", "this should exist" or "I do not understand this"
 * ([ADR 0024](../../../../docs/adr/0024-a-report-is-filed-from-where-the-problem-is-and-fixed-means-a-build.md)), inside Help.
 *
 * Filed over the screen you are on, never from a page you have to find, because the point is that you are looking at
 * the problem while you describe it and the app reads what you are looking at: the route and the tab go with it, the
 * project open in the Studio goes by its id, the API stamps the build, and the reference of the last call that failed
 * is already in the box. The customer types a subject and a body and nothing else — or reads the ones Help drafted
 * from their question, changes what is wrong, and sends it themselves (docs/19).
 */
const KINDS: ReadonlyArray<readonly [ReportKind, string, string]> = [
  ["problem", "Something is broken", "A screen, a video or a number that is not right."],
  ["idea", "Something should exist", "A format, a channel, a button you reached for."],
  ["question", "I do not understand this", "What a screen, a step or a charge means."],
];

export function ReportForm({ where, guide, initial, onClose, onOpenReport, onOpenHelp, onAsk }: {
  where: ReturnType<typeof onScreen>;
  guide: HelpPage | null;
  /** A report Help drafted from a question, for the person to read, change and send. */
  initial?: HelpDraft | null | undefined;
  onClose: () => void;
  onOpenReport: (id: number) => void;
  onOpenHelp?: ((slug: string) => void) | undefined;
  /** Back to asking, when asking is switched on here. */
  onAsk?: (() => void) | undefined;
}) {
  const qc = useQueryClient();
  // Read once, when the form opens: that is the moment it has to be right.
  const [route] = useState(() => `${location.pathname}${location.search}`);
  const [kind, setKind] = useState<ReportKind>(initial?.kind ?? "problem");
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  const [reference, setReference] = useState(() => lastFailureRef() ?? "");
  const file = useMutation({ mutationFn: api.fileReport, onSuccess: () => qc.invalidateQueries({ queryKey: ["reports"] }) });

  const ready = subject.trim() !== "" && body.trim() !== "" && !file.isPending;
  const send = () => file.mutate({
    kind, subject: subject.trim(), body: body.trim(), route,
    ...(where.screen ? { screen: where.screen } : {}),
    ...(where.projectId ? { projectId: where.projectId } : {}),
    ...(reference.trim() ? { reference: reference.trim() } : {}),
  });
  // Said in plain words, so nobody wonders what left their browser.
  const attached = [where.screen ? `the ${where.screen} tab` : "the screen you are on", where.projectId ? "the project open in it" : null, "this version of Joogoos"]
    .filter(Boolean).join(", ");
  const filed = file.data;

  if (filed) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-[13px] text-ink-2 leading-relaxed">
          <b>R-{filed.id}</b> is with us. A person reads every report, and what happens to it shows on the bell beside
          your credit.
        </p>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-medium">Close</button>
          <button onClick={() => onOpenReport(filed.id)} className="px-2 py-1.5 text-xs text-muted hover:text-ink">See it in your reports</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (ready) send(); }} className="flex flex-col gap-3.5">
      <p className="text-[12.5px] text-muted leading-relaxed -mt-1">
        What goes with it: {attached}. You need not describe where you were.
      </p>
      {initial ? (
        <p className="text-[12.5px] bg-tint text-coral-2 rounded-[10px] px-3 py-2 leading-relaxed">
          Help wrote this from what you asked. Read it, change anything that is not what you mean, and send it if it is.
        </p>
      ) : null}
      {guide ? (
        <p className="text-[12.5px] text-muted leading-relaxed -mt-2">
          How this screen works:{" "}
          {onOpenHelp
            ? <button type="button" onClick={() => onOpenHelp(guide.slug)} className="font-medium text-coral hover:text-coral-2">{guide.title}</button>
            : <b className="font-medium text-ink-2">{guide.title}</b>}
        </p>
      ) : null}

      <fieldset className="flex flex-col gap-1.5">
        <legend className="sr-only">What kind of report</legend>
        {KINDS.map(([value, label, hint]) => (
          <label
            key={value}
            className={`flex items-baseline gap-2.5 px-3 py-2 rounded-[10px] border cursor-pointer ${kind === value ? "border-ink bg-panel" : "border-line bg-panel/60"}`}
          >
            <input type="radio" name="kind" value={value} checked={kind === value} onChange={() => setKind(value)} className="accent-coral translate-y-0.5" />
            <span className="text-[13px] font-medium shrink-0">{label}</span>
            <span className="text-[11.5px] text-muted">{hint}</span>
          </label>
        ))}
      </fieldset>

      <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
        In a line
        <input
          autoFocus value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={REPORT_LIMITS.subject}
          placeholder="The video stops at the third shot"
          className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px] font-normal"
        />
      </label>
      <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
        What happened
        <textarea
          value={body} onChange={(e) => setBody(e.target.value)} rows={5} maxLength={REPORT_LIMITS.body}
          placeholder="What you did, what you expected, and what you saw instead."
          className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px] font-normal resize-y"
        />
      </label>
      <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
        Reference, if a screen showed one
        <input
          value={reference} onChange={(e) => setReference(e.target.value)} maxLength={REPORT_LIMITS.reference}
          placeholder="ref 123456789"
          className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px] font-normal font-mono"
        />
      </label>

      {file.error ? <p role="alert" className="text-[12px] text-bad">{String(file.error)}</p> : null}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={!ready} className="px-3.5 py-1.5 rounded-lg bg-ink text-white text-xs font-medium disabled:opacity-60">
          {file.isPending ? "Sending…" : "Send"}
        </button>
        <button type="button" onClick={onClose} className="px-2 py-1.5 text-xs text-muted hover:text-ink">Cancel</button>
        {onAsk ? <button type="button" onClick={onAsk} className="ml-auto px-2 py-1.5 text-xs text-muted hover:text-ink">Ask a question instead</button> : null}
      </div>
    </form>
  );
}
